import ExportStatus from "@/components/ExportStatus";
import LoadingRow from "@/components/LoadingRow";
import EditExportIcon from "@/icons/EditExportIcon";
import { type Export } from "@/models/recording";
import {
  ArticleFilledIcon,
  Breakpoint,
  ButtonSize,
  ClockFilledIcon,
  Color,
  HeadingS,
  HorizontalFlex,
  IconButton,
  IconSize,
  ListHeadingModel,
  ListRowModel,
  RecordVoiceIcon,
  SorterModel,
  Space,
  VerticalFlex,
} from "@enxcs/dap-ui-stratos";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getExportPageTypeText } from "./constants";
import { IconBackground, SubStyledTableBody, SubStyledTableHead, SubTableStyled } from "./RecordingListSubTable.styled";

interface PropsTypes {
  recordingId: string;
  recordingTitle: string;
  exports?: Export[];
  params?: Record<string, string | string[] | number | boolean | SorterModel | undefined>;
}

const RecordingListSubTable: FC<PropsTypes> = ({ recordingId, exports = [], params }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Five headings/cells (with two blank spacers) so the status and action columns line up
  // under the parent RecordingListTable's status and actions columns.
  const exportsHeadings: ListHeadingModel[] = useMemo(
    () => [
      {
        content: t("recordsList.titleColumn"),
      },
      {
        content: "",
      },
      {
        content: "",
      },
      {
        content: t("recordsList.statusColumn"),
      },
      {
        content: "",
      },
    ],
    [t],
  );

  const onEditExportClick = (recordingId: string, type: string, exportId: string) => {
    navigate(`export/${type}/${recordingId}/${exportId}`, {
      state: { ...params },
    });
  };

  const getExportTypeText = useMemo(
    () => [
      t("editAndExportScreen.transcriptTitle"),
      t("editAndExportScreen.minutesTitle"),
      t("editAndExportScreen.summaryTitle"),
      t("editAndExportScreen.detailedMinutesTitle"),
    ],
    [t],
  );

  const getExportTypeIcon = useMemo(
    () => [
      <RecordVoiceIcon size={IconSize.V24} tone={Color.Teal.V30} />,
      <ClockFilledIcon size={IconSize.V24} tone={Color.Teal.V30} />,
      <ArticleFilledIcon size={IconSize.V24} tone={Color.Teal.V30} />,
      <ClockFilledIcon size={IconSize.V24} tone={Color.Teal.V30} />,
    ],
    [],
  );

  const exportsRows: ListRowModel[] = exports.map((item, idx) => ({
    key: `sub-row-${idx}`,
    cells: [
      {
        content: (
          <HorizontalFlex $gap={Space.V4}>
            <IconBackground>{getExportTypeIcon[item.exportType]}</IconBackground> {getExportTypeText[item.exportType]}
          </HorizontalFlex>
        ),
      },
      {
        content: "",
        breakpoint: Breakpoint.L,
      },
      {
        content: "",
        breakpoint: Breakpoint.L,
      },
      {
        content: (
          <HorizontalFlex $justify="flex-start">
            <ExportStatus currentStatus={item.status} />
          </HorizontalFlex>
        ),
      },
      {
        content:
          (item.status === 0 && <LoadingRow />) ||
          ((item.status === 1 || item.status === 3) && (
            <HorizontalFlex $justify="flex-end">
              <IconButton
                icon={EditExportIcon}
                title={t("buttons.edit")}
                size={ButtonSize.XS}
                onClick={() => {
                  onEditExportClick(recordingId, getExportPageTypeText[item.exportType], item.id);
                }}
              />
            </HorizontalFlex>
          )) ||
          (item.status === 2 && <></>),
      },
    ],
  }));

  return (
    <VerticalFlex $gap={Space.V12} $padBottom={Space.V16}>
      <HeadingS>{t("recordsList.exportTableTitle")}</HeadingS>

      <SubTableStyled role="table">
        <SubStyledTableHead headings={exportsHeadings} />

        <SubStyledTableBody headings={exportsHeadings} rows={exportsRows} />
      </SubTableStyled>
    </VerticalFlex>
  );
};

export default RecordingListSubTable;
